// /tasks — vista global de tareas con tabs por urgencia + create/edit.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CalendarDays,
  CalendarOff,
  CheckCircle2,
  ListChecks,
  Flag,
  ChevronDown,
  Plus,
  Pencil,
} from 'lucide-react';
import { currentUser } from '~/lib/pb';
import { useTenantUsers, useToggleTaskDone } from '~/lib/queries';
import {
  useTasksList,
  useTaskTabCounts,
  TASK_TAB_LABEL,
  type TaskTab,
  type TaskAssigneeFilter,
} from '~/lib/tasks-queries';
import { TaskFormModal, TASK_TYPE_LABEL } from '~/components/TaskFormModal/TaskFormModal';
import type { Task, TaskType } from '~/lib/types';
import styles from './_authed.tasks.module.css';

export const Route = createFileRoute('/_authed/tasks')({
  validateSearch: (search: Record<string, unknown>): { tab?: TaskTab } => {
    const tab = search.tab;
    if (
      tab === 'overdue' ||
      tab === 'today' ||
      tab === 'this_week' ||
      tab === 'no_date' ||
      tab === 'completed'
    ) {
      return { tab };
    }
    return {};
  },
  component: TasksPage,
});

function taskCountLine(tab: TaskTab, count: number): string {
  const n = count === 1 ? '1 tarea' : count === 0 ? 'Ninguna tarea' : `${count} tareas`;
  switch (tab) {
    case 'today':
      return `${n} para hoy`;
    case 'overdue':
      return count === 1
        ? '1 tarea vencida'
        : count === 0
          ? 'Ninguna tarea vencida'
          : `${count} tareas vencidas`;
    case 'this_week':
      return `${n} esta semana`;
    case 'no_date':
      return `${n} sin fecha`;
    case 'completed':
      return count === 1
        ? '1 tarea completada'
        : count === 0
          ? 'Ninguna tarea completada'
          : `${count} tareas completadas`;
  }
}

const TAB_ORDER: TaskTab[] = ['overdue', 'today', 'this_week', 'no_date', 'completed'];

const TAB_ICON: Record<TaskTab, typeof AlertTriangle> = {
  overdue: AlertTriangle,
  today: Calendar,
  this_week: CalendarDays,
  no_date: CalendarOff,
  completed: CheckCircle2,
};

const PRIORITY_LABEL: Record<NonNullable<Task['priority']>, string> = {
  low: 'Baja',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

const PRIORITY_VARIANT: Record<NonNullable<Task['priority']>, string> = {
  low: 'priority_low',
  normal: 'priority_normal',
  high: 'priority_high',
  urgent: 'priority_urgent',
};

function formatDueDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Mañana';
  if (diffDays === -1) return 'Ayer';
  if (diffDays < 0) return `Hace ${Math.abs(diffDays)} días`;
  if (diffDays < 7) return `En ${diffDays} días`;
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

function contactLabel(task: Task): string | null {
  const c = task.expand?.contact;
  if (!c) return null;
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return name || c.email || c.phone || null;
}

function TasksPage() {
  const me = currentUser();
  const isSuperAdmin = me?.role === 'superadmin';

  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab: TaskTab = search.tab ?? 'today';
  function setTab(next: TaskTab) {
    navigate({ search: next === 'today' ? {} : { tab: next } });
  }
  const [assignee, setAssignee] = useState<TaskAssigneeFilter>(isSuperAdmin ? 'all' : 'me');
  const [priority, setPriority] = useState<'' | Task['priority']>('');
  const [typeFilter, setTypeFilter] = useState<'' | TaskType>('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);

  const { data: users } = useTenantUsers();
  const { data: counts } = useTaskTabCounts({
    assigneeFilter: assignee,
    priorityFilter: priority,
    typeFilter,
  });
  const { data, isLoading, isError } = useTasksList({
    tab,
    assigneeFilter: assignee,
    priorityFilter: priority,
    typeFilter,
  });

  const toggleDone = useToggleTaskDone();

  const tasks = data?.items ?? [];
  const totalItems = data?.totalItems ?? 0;

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Tareas</h1>
          <p className="page-header__subtitle">{taskCountLine(tab, totalItems)}</p>
        </div>
        <div className="page-header__actions">
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            <Plus size={16} aria-hidden /> Nueva tarea
          </button>
        </div>
      </header>

      <nav className={styles.tabs} aria-label="Filtros de urgencia">
        {TAB_ORDER.map((t) => {
          const Icon = TAB_ICON[t];
          const count = counts?.[t] ?? 0;
          const active = tab === t;
          return (
            <button
              key={t}
              type="button"
              className={active ? `${styles.tab} ${styles.tab__active}` : styles.tab}
              onClick={() => setTab(t)}
            >
              <Icon size={14} aria-hidden />
              {TASK_TAB_LABEL[t]}
              <span
                className={
                  count === 0
                    ? styles.tabBadge
                    : t === 'overdue' && count > 0
                      ? `${styles.tabBadge} ${styles.tabBadgeWarn}`
                      : `${styles.tabBadge} ${styles.tabBadgeActive}`
                }
              >
                {count}
              </span>
            </button>
          );
        })}
      </nav>

      <div className={styles.filters}>
        <label className={styles.filter}>
          <span className={styles.filterLabel}>Asignada a</span>
          <div className={styles.selectWrap}>
            <select
              className={styles.select}
              value={assignee}
              onChange={(e) => setAssignee(e.target.value as TaskAssigneeFilter)}
            >
              <option value="me">Yo</option>
              {isSuperAdmin && <option value="all">Todos</option>}
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name || u.name || u.email}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectIcon} aria-hidden />
          </div>
        </label>

        <label className={styles.filter}>
          <span className={styles.filterLabel}>Prioridad</span>
          <div className={styles.selectWrap}>
            <select
              className={styles.select}
              value={priority ?? ''}
              onChange={(e) => setPriority(e.target.value as '' | Task['priority'])}
            >
              <option value="">Todas</option>
              <option value="urgent">Urgente</option>
              <option value="high">Alta</option>
              <option value="normal">Normal</option>
              <option value="low">Baja</option>
            </select>
            <ChevronDown size={14} className={styles.selectIcon} aria-hidden />
          </div>
        </label>

        <label className={styles.filter}>
          <span className={styles.filterLabel}>Tipo</span>
          <div className={styles.selectWrap}>
            <select
              className={styles.select}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as '' | TaskType)}
            >
              <option value="">Todos</option>
              {(Object.keys(TASK_TYPE_LABEL) as TaskType[]).map((t) => (
                <option key={t} value={t}>
                  {TASK_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectIcon} aria-hidden />
          </div>
        </label>
      </div>

      {isLoading ? (
        <div className="empty-state">Cargando…</div>
      ) : isError ? (
        <div className="empty-state">
          No pudimos cargar las tareas. Recarga o intenta más tarde.
        </div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          {tab === 'overdue' ? (
            <>
              <CheckCircle2
                size={28}
                aria-hidden
                className="empty-state__icon"
                style={{ color: 'var(--color-brand-green)' }}
              />
              <p>Sin tareas vencidas. Buen trabajo.</p>
            </>
          ) : (
            <>
              <ListChecks size={28} aria-hidden className="empty-state__icon" />
              <p>{taskCountLine(tab, 0)}.</p>
            </>
          )}
        </div>
      ) : (
        <ul className={styles.taskList}>
          {tasks.map((task) => {
            const isDone = task.status === 'done' || task.status === 'cancelled';
            const overdue =
              task.due_date && !isDone && new Date(task.due_date).getTime() < Date.now();
            const assigneeUser = task.expand?.assignee;
            const assigneeName =
              assigneeUser?.display_name ||
              assigneeUser?.name ||
              assigneeUser?.email ||
              'Sin asignar';
            const taskType = task.type || 'todo';
            const cLabel = contactLabel(task);

            return (
              <li
                key={task.id}
                className={isDone ? `${styles.taskItem} ${styles.taskItem_done}` : styles.taskItem}
              >
                <button
                  type="button"
                  className={
                    isDone ? `${styles.checkbox} ${styles.checkbox_done}` : styles.checkbox
                  }
                  onClick={() => toggleDone.mutate({ task, done: !isDone })}
                  aria-label={isDone ? 'Reabrir tarea' : 'Marcar como completada'}
                >
                  {isDone && <CheckCircle2 size={14} aria-hidden />}
                </button>

                <div className={styles.taskMain}>
                  <div className={styles.taskHead}>
                    <button
                      type="button"
                      className={styles.taskTitleBtn}
                      onClick={() => setEditing(task)}
                    >
                      {task.title}
                    </button>

                    {taskType !== 'todo' && (
                      <span className={styles.typeBadge}>{TASK_TYPE_LABEL[taskType]}</span>
                    )}

                    {task.priority && task.priority !== 'normal' && (
                      <span
                        className={`${styles.priorityBadge} ${styles[PRIORITY_VARIANT[task.priority]]!}`}
                      >
                        <Flag size={10} aria-hidden /> {PRIORITY_LABEL[task.priority]}
                      </span>
                    )}
                  </div>

                  {task.description ? (
                    <p className={styles.taskDesc}>
                      {task.description.replace(/<[^>]+>/g, '').slice(0, 140)}
                    </p>
                  ) : null}

                  <div className={styles.taskMeta}>
                    {task.due_date && (
                      <span
                        className={
                          overdue
                            ? `${styles.taskDate} ${styles.taskDate_overdue}`
                            : styles.taskDate
                        }
                      >
                        <Calendar size={12} aria-hidden /> {formatDueDate(task.due_date)}
                      </span>
                    )}
                    <span className={styles.taskAssignee}>{assigneeName}</span>
                    {task.lead && (
                      <Link
                        to="/oportunidades/$leadId"
                        params={{ leadId: task.lead }}
                        className={styles.leadLink}
                      >
                        Ver oportunidad →
                      </Link>
                    )}
                    {!task.lead && task.contact && cLabel && (
                      <Link
                        to="/contacts/$contactId"
                        params={{ contactId: task.contact }}
                        className={styles.leadLink}
                      >
                        {cLabel} →
                      </Link>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.editBtn}
                  onClick={() => setEditing(task)}
                  aria-label="Editar tarea"
                  title="Editar"
                >
                  <Pencil size={14} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {creating && <TaskFormModal onClose={() => setCreating(false)} />}
      {editing && <TaskFormModal task={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
